import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { format, isToday, isYesterday, isSameDay } from 'date-fns';

interface Conversation {
  id?: string; // Optional - staff conversation might not have an ID
  friend: {
    id: string;
    firstName: string;
    lastName: string;
    avatarUrl?: string;
  };
  lastMessage?: {
    content: string;
    createdAt: string;
    senderId: string;
  };
  unreadCount: number;
  isStaff?: boolean; // Flag to identify staff conversation
  updatedAt?: string; // For sorting
}

interface Message {
  id: string;
  content: string;
  senderId: string;
  recipientId: string;
  read?: boolean;
  readAt?: string | null;
  createdAt: string;
  sender?: {
    id: string;
    firstName: string;
    lastName: string;
    avatarUrl?: string;
  };
}

const MessagesPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [messageText, setMessageText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [friendDetails, setFriendDetails] = useState<{ id: string; firstName: string; lastName: string; avatarUrl?: string } | null>(null);
  const messageInputRef = useRef<HTMLInputElement>(null);

  // Helper function to format date labels for message separators (WhatsApp-style)
  const formatDateLabel = (date: Date): string => {
    if (isToday(date)) {
      return t('messages.today');
    }
    if (isYesterday(date)) {
      return t('messages.yesterday');
    }
    // For dates within the last week, show day name + date
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays < 7) {
      return format(date, 'EEEE, MMM d'); // e.g., "Monday, Jan 6"
    }
    // For older dates, show full date with year
    return format(date, 'MMM d, yyyy'); // e.g., "Jan 6, 2025"
  };

  useEffect(() => {
    fetchConversations();
  }, []);

  // Handle friendId query parameter - select conversation when conversations are loaded
  useEffect(() => {
    const friendId = searchParams.get('friendId');
    if (friendId && !loading) {
      // Check if conversation exists in the list
      const conversation = conversations.find(conv => conv.friend.id === friendId);
      if (conversation) {
        // Conversation exists - select it
        setSelectedConversation(friendId);
      } else {
        // Conversation doesn't exist yet (new conversation)
        // Still select it - messages will be fetched and user can send
        setSelectedConversation(friendId);
      }
      // Clear the query parameter after selecting
      setSearchParams({});
    }
  }, [conversations, loading, searchParams, setSearchParams]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation);
      // Check if we have friend details in conversations, if not fetch them
      const conversation = conversations.find(conv => conv.friend.id === selectedConversation);
      if (conversation) {
        setFriendDetails(conversation.friend);
      } else {
        // Conversation doesn't exist yet - fetch friend details
        fetchFriendDetails(selectedConversation);
      }
      // Focus the message input when a conversation is selected
      setTimeout(() => {
        messageInputRef.current?.focus();
      }, 100);
    }
  }, [selectedConversation, conversations]);

  const fetchFriendDetails = async (friendId: string) => {
    try {
      // Get friend from friends list
      const response = await api.get('/friends');
      const friend = response.data.friends?.find((f: any) => f.id === friendId);
      if (friend) {
        setFriendDetails({
          id: friend.id,
          firstName: friend.firstName,
          lastName: friend.lastName,
          avatarUrl: friend.avatarUrl,
        });
      }
    } catch (error) {
      console.error('Error fetching friend details:', error);
    }
  };

  const fetchConversations = async () => {
    try {
      const response = await api.get('/messages');
      const conversations = response.data.conversations || [];
      
      // Sort: staff conversation first, then others by last message time
      const sorted = conversations.sort((a: Conversation, b: Conversation) => {
        // Staff conversation always first
        if (a.isStaff && !b.isStaff) return -1;
        if (!a.isStaff && b.isStaff) return 1;
        // Otherwise sort by last message time
        const aTime = a.lastMessage?.createdAt || a.updatedAt || '0';
        const bTime = b.lastMessage?.createdAt || b.updatedAt || '0';
        return new Date(bTime).getTime() - new Date(aTime).getTime();
      });
      
      setConversations(sorted);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (friendId: string) => {
    try {
      const response = await api.get(`/messages/${friendId}`, {
        params: { page: 1, limit: 50 },
      });
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleRefresh = () => {
    fetchConversations();
    if (selectedConversation) {
      fetchMessages(selectedConversation);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedConversation || !messageText.trim()) return;

    setSending(true);
    try {
      await api.post('/messages', {
        recipientId: selectedConversation,
        content: messageText,
      });
      setMessageText('');
      fetchMessages(selectedConversation);
      fetchConversations(); // Refresh to update last message
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Get friend details from conversations or from friendDetails state
  const selectedFriend = conversations.find(
    (conv) => conv.friend.id === selectedConversation
  )?.friend || friendDetails;

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 h-[calc(100vh-8rem)] flex">
      {/* Conversations List */}
      <div className="w-1/3 border-r border-gray-200 pr-4">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-xl font-bold text-gray-900">{t('messages.conversations')}</h1>
          <button
            onClick={handleRefresh}
            className="text-sm text-primary-600 hover:text-primary-700"
          >
            {t('messages.refresh')}
          </button>
        </div>
        <div className="space-y-2 overflow-y-auto max-h-[calc(100vh-12rem)]">
          {conversations.length === 0 ? (
            <p className="text-gray-500 text-center py-8">{t('messages.noConversations')}</p>
          ) : (
            conversations.map((conversation) => {
              // Display name: "RideConnect Staff" for staff, otherwise actual name
              const displayName = conversation.isStaff 
                ? 'RideConnect Staff'
                : `${conversation.friend.firstName} ${conversation.friend.lastName}`;
              
              return (
                <button
                  key={conversation.friend.id}
                  onClick={() => setSelectedConversation(conversation.friend.id)}
                  className={`w-full text-left p-3 rounded-lg hover:bg-gray-100 ${
                    selectedConversation === conversation.friend.id ? 'bg-primary-50' : ''
                  } ${conversation.isStaff ? 'border-l-4 border-primary-600 bg-primary-50/30' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium truncate">
                          {displayName}
                        </h3>
                        {conversation.isStaff && (
                          <span className="flex-shrink-0 text-xs bg-primary-100 text-primary-800 px-2 py-0.5 rounded-full">
                            Support
                          </span>
                        )}
                      </div>
                      {conversation.lastMessage && (
                        <p className="text-sm text-gray-500 truncate">
                          {conversation.lastMessage.content}
                        </p>
                      )}
                    </div>
                    {conversation.unreadCount > 0 && (
                      <span className="flex-shrink-0 bg-primary-600 text-white text-xs rounded-full px-2 py-1 ml-2">
                        {conversation.unreadCount}
                      </span>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 pl-4 flex flex-col">
        {selectedConversation ? (
          <>
            <div className="border-b border-gray-200 pb-4 mb-4">
              <h2 className="text-lg font-semibold">
                {selectedConversation && conversations.find(c => c.friend.id === selectedConversation)?.isStaff
                  ? 'RideConnect Staff'
                  : selectedFriend
                  ? `${selectedFriend.firstName} ${selectedFriend.lastName}`
                  : 'Loading...'}
              </h2>
            </div>
            <div className="flex-1 overflow-y-auto space-y-4 mb-4">
              {messages.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  {t('messages.noMessages')}
                </div>
              ) : (
                messages.map((message, index) => {
                  // Check if message is from current user
                  const isOwn = message.senderId === user?.id;
                  
                  // Check if we need to show a date separator
                  const messageDate = new Date(message.createdAt);
                  const prevMessage = messages[index - 1];
                  const prevDate = prevMessage ? new Date(prevMessage.createdAt) : null;
                  const showDateSeparator = !prevDate || !isSameDay(messageDate, prevDate);
                  
                  return (
                    <React.Fragment key={message.id}>
                      {/* Date separator */}
                      {showDateSeparator && (
                        <div className="flex justify-center my-4">
                          <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                            {formatDateLabel(messageDate)}
                          </span>
                        </div>
                      )}
                      
                      {/* Message bubble */}
                      <div
                        className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-xs px-4 py-2 rounded-lg ${
                            isOwn
                              ? 'bg-primary-600 text-white'
                              : 'bg-gray-200 text-gray-900'
                          }`}
                        >
                          <p>{message.content}</p>
                          <p className={`text-xs mt-1 ${
                            isOwn ? 'text-primary-100' : 'text-gray-500'
                          }`}>
                            {format(new Date(message.createdAt), 'h:mm a')}
                          </p>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })
              )}
            </div>
            <form onSubmit={handleSendMessage} className="flex space-x-2">
              <input
                ref={messageInputRef}
                type="text"
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                placeholder={t('messages.typeMessage')}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
              />
              <button
                type="submit"
                disabled={sending || !messageText.trim()}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {t('messages.send')}
              </button>
            </form>
          </>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500">
            {t('messages.noConversations')}
          </div>
        )}
      </div>
    </div>
  );
};

export default MessagesPage;
